import { Layer } from './Layer';
import { Text } from './../2DObjects/Text';
import RenderManager from '../rendering/render-manager';
import { applyValidOrder } from './DefaultOrder';
import SlideManager from '../utils/slide-manager';
import Picture from '../2DObjects/Picture';
import Lottie from '../2DObjects/Lottie';
import Highlighter from '../2DObjects/highlighter';
import CooboLink from '../2DObjects/CooboLink';
import Button from '../2DObjects/Button';
import TabExplorer from '../2DObjects/TabExplorer';
import Iframe from '../2DObjects/Iframe';
import Video from '../2DObjects/Video';
import Slider from '../2DObjects/Slider';
import MathFormula from '../2DObjects/MathFormula';
import Wheel from './../2DObjects/Wheel';
import RollerCoaster from '../2DObjects/RollerCoaster';
import CustomizableRegion from '../2DObjects/customizable-region';
import Graphing from '../2DObjects/graphing';
import ImgComparer from "../2DObjects/ImgComparer";
import ImageCarousel from "../2DObjects/ImageCarousel";
import Line from "../2DObjects/Line";

const LayerReorderAction = Object.freeze({
    FRONT: 1,
    FORWARD: 2,
    BACKWARD: 3,
    BACK: 4,
});

const LayersManager = new class LayersManager {
    init(data) {
        this.LayerReorderAction = LayerReorderAction;

        this.container = document.createElement('div');
        this.container.id = 'slide-layers-container';
        // this.container.style.background = Q3.data.slides[0].background || '#fff';
        this.container.style.position = 'relative';
        this.container.style.height = '100%';
        if (!Q3.editMode) {
            this.letterbox = Q3.letterbox;
        }


        this.attachedObjects = [];

        this.hoverDependentObjects = [];

        //to find layers by uuid as in data
        this.list = {};

        //The stack stores every layers,
        //even ones removed from the container (unvisible).
        //Purpose = keep track of indices and parenting
        this.graph = [];

        //output list without parenting and groups
        this.stack = [];

        //init
        this.list["Renderer"] = new Layer({
            name: data.layers.layer3D.name,
            index: 0,
            visible: data.slides[0].layers.Renderer,
            parent: data.layers.layer3D.parent,
            domElement: RenderManager.domElement,
            uuid: "Renderer",
            type: "Renderer"
        });

        this.minimumUserLayerIndex = 1;

        Q3.container.appendChild(this.container);

        window.addEventListener("resize", this._onResize.bind(this));

        return this;
    }

    _onResize(e) { this.forceUpdate(); }
    forceUpdate() { this.graph.forEach(layer => layer.forceUpdate()); }

    getEditorAnimationDelay(data, which = "editor") {
        if (!Q3.editMode) return {};
        let minDelay = 0;
        return Object.keys(data)
            .map(lUuid => ({
                lUuid: lUuid,
                delay: ((Object.values(data[lUuid].objects)[0] || {}).animation || { delay: 0 }).delay
            }))
            .sort((a, b) => a.delay > b.delay ? 1 : -1)
            .reduce((acc, current, i, arr) => {
                if (current.delay > minDelay && i && arr[i - 1].delay !== current.delay) {
                    acc.currentDelay = acc.currentDelay + 0.5;
                }
                acc.editor[current.lUuid] = acc.currentDelay;
                acc.viewer[current.lUuid] = current.delay;
                return acc
            }, { editor: {}, viewer: {}, currentDelay: minDelay })[which]
    }

    addLayers(data) {
        let delays = this.getEditorAnimationDelay(data);
        for (let uuid in data) {

            const layerData = data[uuid];
            if (!layerData.objects) {
                console.warn('Found Layer 2D with "objects" property not set, it will be removed');
                layerData.objects = {};
            }

            const key = Object.keys(layerData.objects)[0];
            if (!key) {
                delete data[uuid];
                continue;
            }

            switch (layerData.objects[key].type) {
                case "iframe":
                    layerData.subtype = "iframes"
                    break;
                case "video":
                    layerData.subtype = "videos"
                    break;
                case "button":
                    layerData.subtype = "button";
                    break;
                case "tabexplorer":
                    layerData.subtype = "tabexplorer";
                    break;
                case "picture":
                    layerData.subtype = "picture";
                    break;
                case "imageCarousel":
                    layerData.subtype = "carousel";
                    break;
                default:
                    layerData.subtype = layerData.subtype || layerData.objects[key].type
            }

            if (layerData.objects[key].type === "iframe") {
                layerData.subtype = "iframes"
            } else if (layerData.objects[key].type === "video") {
                layerData.subtype = "videos"
            }

            this.list[uuid] = new Layer({
                type: layerData.type,
                isText: layerData.objects[key].type === "text" || layerData.objects[key].type === "mathFormula",
                uuid: uuid,
                name: layerData.name,
                index: layerData.index,
                visible: true,
                parent: layerData.parent,
                objects: layerData.objects,
                editorAnimationDelay: delays[uuid] || 0,
                subtype: layerData.subtype,
                reordered: layerData.reordered,
            });
            if (layerData.templateId && layerData.templateUuid) {
                this.list[uuid].templateId = layerData.templateId;
                this.list[uuid].templateUuid = layerData.templateUuid;
            }
            this.list[uuid].level = 1;
        }

        let topLayers = [];
        for (let layer of Object.values(this.list)) {
            if (!layer.parent) topLayers.push(layer);
            if (layer.children) applyValidOrder(layer.children);
        }
        applyValidOrder(topLayers);

    }

    update(slide, byScroll) {

        this.updateGraph();
        this.updateStack();
        this.updateLayersRender(slide, byScroll);

    }

    updateChildrenIndices(childrenArray) {
        const { layer3D, layers2D } = Q3.data.layers;
        let i = 0;


        childrenArray.forEach((c) => {
            c.index = i;
            if (c.uuid === 'Renderer') layer3D.index = i;
            else if (c.type === '2D') {
                if (c.uuid in layers2D) layers2D[c.uuid].index = i;
                else c.index = i + 100;
            }
            i++;
        });
    }

    reorderChildren(childrenArray, child, action) {

        let oldIndex = childrenArray.indexOf(child);
        let newIndex;

        switch (action) {
            case LayerReorderAction.FRONT:
                newIndex = childrenArray.length - 1;
                break;
            case LayerReorderAction.BACK:
                newIndex = 0;
                break;
            case LayerReorderAction.FORWARD:
                newIndex = oldIndex + 1;
                break;
            case LayerReorderAction.BACKWARD:
                newIndex = oldIndex - 1;
                break;
        }

        let minIndex = child.parent ? 0 : this.minimumUserLayerIndex;
        newIndex = Math.min(Math.max(minIndex, newIndex), childrenArray.length - 1);
        if (newIndex === oldIndex) return;

        childrenArray.splice(oldIndex, 1);
        childrenArray.splice(newIndex, 0, child);

        if (child.type === '2D') {
            Q3.data.layers.layers2D[child.uuid].reordered = true;
            child.reordered = true;
        }

    }

    updateGraph() {

        //reset graph
        this.graph = [];

        //reset parenting
        // for (let layer in this.list)

        Object.keys(this.list).forEach(uuid => {

            //reset parenting
            if (this.list[uuid].children)
                this.list[uuid].children = [];

            //check current visibilities
            if (!this.list[uuid].isTemporary)
                this.list[uuid].visible = SlideManager.currentSlide.layers[uuid];
            else {
                this.list[uuid].visible = this.list[uuid].visible === undefined ? true : this.list[uuid].visible;
            }
        });

        //create new graph
        Object.keys(this.list).forEach(uuid => {

            const layer = this.list[uuid];

            if (layer.parent) {
                const parentPlayer = this.list[layer.parent];
                if (parentPlayer) {
                    parentPlayer.add(layer);
                    layer.isVisible = parentPlayer.isVisible;
                }
            } else {
                this.graph[layer.index] = layer;
            }

        });

        this.graph = this.graph.filter(x => x);

    }

    updateStack() {

        this.stack = [];

        //update levels + build stack
        const traverseLevels = (a, level = 0) => {
            for (let i = 0; i < a.length; i++) {
                if (!a[i]) continue;
                a[i].level = level;
                if (a[i].type !== 'Renderer' && !a[i].visible) continue;
                if (a[i].type === '2DGroup') traverseLevels(a[i].children, level + 1);
                else {
                    if (this.stack.indexOf(a[i]) === -1) {
                        this.stack.push(a[i]);
                    }
                }

            }
        }

        traverseLevels(this.graph);

    }

    updateLayersRender(slide, byScroll) {

        if (slide && slide === SlideManager.activeSlide) {
            this.letterbox && setNodeChildren(this.letterbox, []);
            setNodeChildren(this.container, this.stack.map(layer => layer.domElement));
        } else {
            let layersToKeep = [];
            //browser DOM rendering efficiency here ?

            let slideData = SlideManager.currentSlide;
            let prevSlideData = Q3.data.slides[SlideManager.prevSlide];

            let newChildren = { container: new ChildList(this.container.children) };
            if (this.letterbox) {
                newChildren.letterbox = new ChildList(this.letterbox.children);
            }

            for (let cnt in newChildren) {
                const children = this[cnt].children;
                const childrenArray = Array.prototype.slice.call(children)

                childrenArray.forEach((child) => {
                    if (child.id !== '' && layersToKeep.indexOf(child.id) > -1);
                    else {
                        let uuid = child.id === "main-canvas" ? "Renderer" : child.id;
                        let layer = this.list[uuid];
                        let entering = layer && slideData.layers[uuid];
                        let leaving = prevSlideData && prevSlideData.layers[uuid] && layer && layer.leavingAnimationDelay;
                        if (!entering && !leaving) {
                            let cntChildren = newChildren[cnt];
                            cntChildren.remove(child);
                        }
                    }
                });
            };


            for (let [i, layer] of this.stack.entries()) {
                let object = layer.objects && layer.objects[0] ? layer.objects[0] : null;
                if (object) {
                    if (this.isLetterBoxObj(object) && !Q3.editMode && object.inLetterbox !== false && !object.targetUuid) {
                        newChildren.letterbox.append(layer.domElement);
                        if (object.onReadyToAnimate) object.onReadyToAnimate();
                        layer.appended = true;
                    } else {
                        newChildren.container.append(layer.domElement);
                        if (object.onReadyToAnimate) object.onReadyToAnimate();
                        layer.appended = true;
                    }

                } else {
                    newChildren.container.append(layer.domElement);
                    layer.appended = true;
                }

                layer.domElement.style.zIndex = 5 + i;

            };

            for (let cnt in newChildren) {
                setNodeChildren(this[cnt], newChildren[cnt].children);
            }


        }

        this._update3DLayerVisibility();
        Q3.Events.fire("layersUpdated");
    }

    _update3DLayerVisibility() {
        let visible = this.list['Renderer'].visible;
        let scene = RenderManager.activeScene;
        if (scene.visible === visible) return;

        scene.visible = visible;
        RenderManager.activeCamera.update = true;
    }

    isLetterBoxObj(obj) {
        return (
            (obj instanceof Picture ||
                obj instanceof Text ||
                obj instanceof Lottie ||
                obj instanceof ImgComparer ||
                obj instanceof Button ||
                obj instanceof TabExplorer ||
                obj instanceof Iframe ||
                obj instanceof Video ||
                obj instanceof Slider ||
                obj instanceof MathFormula ||
                obj instanceof Wheel ||
                obj instanceof CooboLink ||
                obj instanceof CustomizableRegion ||
                obj instanceof Graphing ||
                obj instanceof Highlighter ||
                obj instanceof ImageCarousel ||
                obj instanceof Line ||
                obj instanceof RollerCoaster) && !obj.targetUuid)
    }

    render() {
        const pr = RenderManager.pixelRatio;
        const { width, height } = RenderManager.domElement;

        this.graph.forEach(layer => layer.render(width / pr, height / pr));
    }

    onMouseMove(event, point) {
        this.graph.forEach(layer => {
            layer.objects.forEach(obj => {
                if (obj.type2d === "CustomizableRegion") {
                    obj.onMouseMove(event, point);
                }
            });
        })
    }

    onMouseUp(event, point) {
        this.graph.forEach(layer => {
            layer.objects.forEach(obj => {
                if (obj.type2d === "CustomizableRegion") {
                    obj.onMouseUp(event, point);
                }
            });
        })
    }

    onMouseDown(event, point) {
        this.graph.forEach(layer => {
            layer.objects.forEach(obj => {
                if (obj.type2d === "CustomizableRegion") {
                    obj.onMouseDown(event, point);
                }
            });
        })
    }
}

export default LayersManager;
export { LayersManager, LayerReorderAction };

class ChildList {
    constructor(children = []) {
        this.children = [...children];
    }

    remove(child) {
        let index = this.children.indexOf(child);
        if (index === -1) return;

        this.children.splice(index, 1);
    }

    append(child) {
        if (this.children[this.children.length - 1] === child) return;
        this.remove(child);
        this.children.push(child);
    }
}

function setNodeChildren(node, children) {
    let childrenSet = new WeakSet(children);

    for (let child of [...node.children]) {
        if (!childrenSet.has(child)) {
            node.removeChild(child);
        }
    }

    for (let i = 0; i < children.length; i++) {
        let prevChild = children[i - 1];
        let child = children[i];

        if (node.children[i] !== child) {
            let nextChild = prevChild ? prevChild.nextSibling : node.firstChild;
            node.insertBefore(child, nextChild);
        }
    }
}
